import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery } from 'react-query';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  DocumentArrowDownIcon,
  PrinterIcon,
  FunnelIcon,
  BanknotesIcon,
  CreditCardIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';

const FinancialReports = ({ stats = {} }) => {
  const { t } = useLanguage();
  const [dateRange, setDateRange] = useState('this-month');
  const [reportType, setReportType] = useState('revenue');

  // Fetch detailed financial data
  const { data: financialData, isLoading } = useQuery(
    ['financial-reports', dateRange],
    async () => {
      const response = await axios.get(`/payments/reports?range=${dateRange}`);
      return response.data;
    }
  );

  const data = financialData || {};

  // Sample data for charts (this would come from the API)
  const monthlyRevenue = [
    { month: 'Jan', revenue: 45000, expenses: 12000, profit: 33000 },
    { month: 'Fév', revenue: 52000, expenses: 13500, profit: 38500 },
    { month: 'Mar', revenue: 48000, expenses: 11800, profit: 36200 },
    { month: 'Avr', revenue: 61000, expenses: 14200, profit: 46800 },
    { month: 'Mai', revenue: 55000, expenses: 13000, profit: 42000 },
    { month: 'Jun', revenue: 67000, expenses: 15500, profit: 51500 },
  ];

  const paymentMethodData = [
    { name: 'Espèces', value: 45, color: '#10B981' },
    { name: 'Carte', value: 30, color: '#3B82F6' },
    { name: 'Virement', value: 20, color: '#8B5CF6' },
    { name: 'Chèque', value: 5, color: '#F59E0B' },
  ];

  const dailyRevenue = [
    { day: 'Lun', amount: 8500 },
    { day: 'Mar', amount: 12000 },
    { day: 'Mer', amount: 9800 },
    { day: 'Jeu', amount: 11500 },
    { day: 'Ven', amount: 15200 },
    { day: 'Sam', amount: 18000 },
    { day: 'Dim', amount: 14500 },
  ];

  const kpiData = {
    totalRevenue: data.totalRevenue || 245000,
    monthlyGrowth: data.monthlyGrowth || 12.5,
    averageTransaction: data.averageTransaction || 8500,
    totalTransactions: data.totalTransactions || 156,
    pendingAmount: data.pendingAmount || 25000,
    overdueAmount: data.overdueAmount || 8500,
  };

  const handleExport = (format) => {
    // This would trigger an API call to generate and download the report
    console.log(`Exporting ${reportType} report in ${format} format for ${dateRange}`);
  };

  const formatCurrency = (amount) => {
    return amount.toLocaleString() + ' DA';
  };

  const getGrowthColor = (growth) => {
    return growth >= 0 ? 'text-green-400' : 'text-red-400';
  };

  const getGrowthIcon = (growth) => {
    return growth >= 0 ? ArrowTrendingUpIcon : ArrowTrendingDownIcon;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header with filters */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div>
            <h2 className="text-2xl font-bold text-white">Rapports Financiers</h2>
            <p className="text-white/60 mt-1">Analyse détaillée de vos performances financières</p>
          </div>
          
          <div className="flex items-center space-x-4">
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="today">Aujourd'hui</option>
              <option value="this-week">Cette semaine</option>
              <option value="this-month">Ce mois</option>
              <option value="last-month">Mois dernier</option>
              <option value="this-quarter">Ce trimestre</option>
              <option value="this-year">Cette année</option>
            </select>

            <select
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
              className="bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="revenue">Revenus</option>
              <option value="payments">Paiements</option>
              <option value="subscriptions">Abonnements</option>
              <option value="members">Membres</option>
            </select>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleExport('pdf')}
                className="p-2 bg-red-500/20 text-red-400 rounded-xl hover:bg-red-500/30 transition-colors"
                title="Exporter en PDF"
              >
                <DocumentArrowDownIcon className="h-5 w-5" />
              </button>
              <button
                onClick={() => handleExport('excel')}
                className="p-2 bg-green-500/20 text-green-400 rounded-xl hover:bg-green-500/30 transition-colors"
                title="Exporter en Excel"
              >
                <DocumentTextIcon className="h-5 w-5" />
              </button>
              <button
                onClick={() => window.print()}
                className="p-2 bg-blue-500/20 text-blue-400 rounded-xl hover:bg-blue-500/30 transition-colors"
                title="Imprimer"
              >
                <PrinterIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm font-medium">Revenus Totaux</p>
              <p className="text-2xl font-bold text-white mt-2">
                {formatCurrency(kpiData.totalRevenue)}
              </p>
              <div className="flex items-center mt-2">
                {React.createElement(getGrowthIcon(kpiData.monthlyGrowth), {
                  className: `h-4 w-4 ${getGrowthColor(kpiData.monthlyGrowth)} mr-1`
                })}
                <span className={`text-sm ${getGrowthColor(kpiData.monthlyGrowth)}`}>
                  {Math.abs(kpiData.monthlyGrowth)}% vs mois dernier
                </span>
              </div>
            </div>
            <div className="p-3 rounded-2xl bg-green-500/20">
              <CurrencyDollarIcon className="h-6 w-6 text-green-400" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm font-medium">Transaction Moyenne</p>
              <p className="text-2xl font-bold text-white mt-2">
                {formatCurrency(kpiData.averageTransaction)}
              </p>
              <p className="text-white/50 text-sm mt-2">
                {kpiData.totalTransactions} transactions
              </p>
            </div>
            <div className="p-3 rounded-2xl bg-blue-500/20">
              <ChartBarIcon className="h-6 w-6 text-blue-400" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm font-medium">En Attente</p>
              <p className="text-2xl font-bold text-white mt-2">
                {formatCurrency(kpiData.pendingAmount)}
              </p>
              <p className="text-red-400 text-sm mt-2">
                {formatCurrency(kpiData.overdueAmount)} en retard
              </p>
            </div>
            <div className="p-3 rounded-2xl bg-yellow-500/20">
              <CalendarIcon className="h-6 w-6 text-yellow-400" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
        >
          <h3 className="text-xl font-semibold text-white mb-4">
            Évolution des Revenus
          </h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyRevenue}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis dataKey="month" stroke="rgba(255,255,255,0.6)" />
                <YAxis stroke="rgba(255,255,255,0.6)" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'rgba(0,0,0,0.8)', 
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '12px',
                    color: 'white'
                  }}
                />
                <Legend />
                <Bar dataKey="revenue" fill="#3B82F6" name="Revenus" />
                <Bar dataKey="profit" fill="#10B981" name="Profit" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Payment Methods Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
        >
          <h3 className="text-xl font-semibold text-white mb-4">
            Répartition par Mode de Paiement
          </h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={paymentMethodData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {paymentMethodData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'rgba(0,0,0,0.8)', 
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '12px',
                    color: 'white'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Daily Revenue Trend */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 lg:col-span-2"
        >
          <h3 className="text-xl font-semibold text-white mb-4">
            Revenus Quotidiens (Cette Semaine)
          </h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={dailyRevenue}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis dataKey="day" stroke="rgba(255,255,255,0.6)" />
                <YAxis stroke="rgba(255,255,255,0.6)" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'rgba(0,0,0,0.8)', 
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '12px',
                    color: 'white'
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="amount" 
                  stroke="#8B5CF6" 
                  strokeWidth={3}
                  dot={{ fill: '#8B5CF6', strokeWidth: 2, r: 6 }}
                  activeDot={{ r: 8, stroke: '#8B5CF6', strokeWidth: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      </div>

      {/* Summary Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
      >
        <h3 className="text-xl font-semibold text-white mb-4">
          Résumé Détaillé
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-white">
            <thead>
              <tr className="border-b border-white/20">
                <th className="text-left py-3 px-4">Métrique</th>
                <th className="text-right py-3 px-4">Valeur</th>
                <th className="text-right py-3 px-4">Évolution</th>
                <th className="text-right py-3 px-4">Objectif</th>
              </tr>
            </thead>
            <tbody className="text-white/80">
              <tr className="border-b border-white/10">
                <td className="py-3 px-4">Revenus totaux</td>
                <td className="text-right py-3 px-4 font-semibold">{formatCurrency(kpiData.totalRevenue)}</td>
                <td className="text-right py-3 px-4 text-green-400">+12.5%</td>
                <td className="text-right py-3 px-4">250,000 DA</td>
              </tr>
              <tr className="border-b border-white/10">
                <td className="py-3 px-4">Nombre de transactions</td>
                <td className="text-right py-3 px-4 font-semibold">{kpiData.totalTransactions}</td>
                <td className="text-right py-3 px-4 text-green-400">+8.2%</td>
                <td className="text-right py-3 px-4">200</td>
              </tr>
              <tr className="border-b border-white/10">
                <td className="py-3 px-4">Transaction moyenne</td>
                <td className="text-right py-3 px-4 font-semibold">{formatCurrency(kpiData.averageTransaction)}</td>
                <td className="text-right py-3 px-4 text-green-400">+4.1%</td>
                <td className="text-right py-3 px-4">9,000 DA</td>
              </tr>
              <tr className="border-b border-white/10">
                <td className="py-3 px-4">Paiements en attente</td>
                <td className="text-right py-3 px-4 font-semibold">{formatCurrency(kpiData.pendingAmount)}</td>
                <td className="text-right py-3 px-4 text-red-400">+15.3%</td>
                <td className="text-right py-3 px-4">20,000 DA</td>
              </tr>
              <tr>
                <td className="py-3 px-4">Paiements en retard</td>
                <td className="text-right py-3 px-4 font-semibold">{formatCurrency(kpiData.overdueAmount)}</td>
                <td className="text-right py-3 px-4 text-red-400">-5.2%</td>
                <td className="text-right py-3 px-4">5,000 DA</td>
              </tr>
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Recommendations */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
      >
        <h3 className="text-xl font-semibold text-white mb-4">
          Recommandations
        </h3>
        <div className="space-y-4">
          <div className="flex items-start space-x-3 p-3 bg-blue-500/20 rounded-xl">
            <ArrowTrendingUpIcon className="h-5 w-5 text-blue-400 mt-1" />
            <div>
              <p className="text-white font-medium">Croissance positive</p>
              <p className="text-white/70 text-sm">
                Vos revenus sont en hausse de 12.5% ce mois. Continuez sur cette lancée!
              </p>
            </div>
          </div>
          
          <div className="flex items-start space-x-3 p-3 bg-yellow-500/20 rounded-xl">
            <CalendarIcon className="h-5 w-5 text-yellow-400 mt-1" />
            <div>
              <p className="text-white font-medium">Suivi des paiements</p>
              <p className="text-white/70 text-sm">
                25,000 DA en paiements en attente. Considérez un suivi automatique.
              </p>
            </div>
          </div>
          
          <div className="flex items-start space-x-3 p-3 bg-green-500/20 rounded-xl">
            <BanknotesIcon className="h-5 w-5 text-green-400 mt-1" />
            <div>
              <p className="text-white font-medium">Diversification des paiements</p>
              <p className="text-white/70 text-sm">
                45% des paiements sont en espèces. Encouragez les paiements numériques.
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default FinancialReports; 